import React, {useState, useRef, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../hooks/useAuth';
import {useEvents} from '../hooks/useEvents';
import {useDarkMode} from '../hooks/useDarkMode';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faUsers,
    faSpinner,
    faTimes,
    faCalendarAlt,
    faPaste,
    faKeyboard
} from '@fortawesome/free-solid-svg-icons';
import AuthRequired from '../components/common/AuthRequired';
import AppHeader from '../components/common/AppHeader';
import {generateId} from '../utils/helpers';
import {useUser, getDisplayName} from "../hooks/useUser";
import {ref, set} from 'firebase/database';
import {database} from '../firebase/firebase';
import {AnonymousUser} from '../types';
import { useTranslation } from 'react-i18next';

const CreateEventPage: React.FC = () => {
    const {user} = useAuth();
    const {createEvent} = useEvents();
    const {darkMode, toggleDarkMode} = useDarkMode();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [eventName, setEventName] = useState('');
    const [description, setDescription] = useState('');
    const [participants, setParticipants] = useState<string[]>([]);
    const [currentInput, setCurrentInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [ownerName, setOwnerName] = useState('');
    const [ownerNameEdited, setOwnerNameEdited] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const {userProfile, getOrCreateEventUser} = useUser();

    // Keep the owner seat on the profile name until the person types their own.
    // The profile arrives a moment after auth does, so filling this in once — the
    // first time `user` existed — pinned whatever was known then, i.e. the Google name.
    useEffect(() => {
        if (!user || ownerNameEdited) return;
        setOwnerName(getDisplayName(userProfile, user));
    }, [user, userProfile, ownerNameEdited]);

    // Focus input when component mounts
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const addParticipant = (name: string) => {
        const trimmedName = name.trim();
        if (trimmedName && !participants.includes(trimmedName)) {
            setParticipants(prev => [...prev, trimmedName]);
        }
    };

    const removeParticipant = (index: number) => {
        setParticipants(participants.filter((_, i) => i !== index));
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && currentInput.trim()) {
            e.preventDefault();
            addParticipant(currentInput);
            setCurrentInput('');
        } else if (e.key === 'Backspace' && !currentInput && participants.length > 0) {
            // Remove last participant when backspace on empty input
            setParticipants(prev => prev.slice(0, -1));
        }
    };

    const handleInputChange = (value: string) => {
        setCurrentInput(value);

        // Auto-add if comma or semicolon detected (for bulk paste)
        if (value.includes(',') || value.includes(';')) {
            const names = value.split(/[,;]/).map(n => n.trim()).filter(n => n);
            names.forEach(name => addParticipant(name));
            setCurrentInput('');
        }
    };

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const names = text.split(/[\n,;]/).map(n => n.trim()).filter(n => n);
            names.forEach(name => addParticipant(name));
            setCurrentInput('');
        } catch (error) {
            console.error('Failed to paste from clipboard:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!user) return;

        // Add current input if not empty
        if (currentInput.trim()) {
            addParticipant(currentInput);
        }

        const finalParticipants = currentInput.trim()
            ? [...participants, currentInput.trim()]
            : participants;

        if (!eventName.trim() || finalParticipants.length === 0) {
            alert(t('createEvent.errors.nameRequired'));
            return;
        }

        setIsSubmitting(true);

        try {
            const eventId = `ev_${generateId()}`;

            const finalOwnerName = ownerName.trim() || getDisplayName(userProfile, user);
            const participantsMap: { [userId: string]: string } = {
                [user.uid]: finalOwnerName
            };


            for (const name of finalParticipants) {
                const anonymousId = `~u_${generateId()}`;
                participantsMap[anonymousId] = name.trim();

                const anonymousUser: AnonymousUser = {
                    anonymousId,
                    displayName: name.trim(),
                    createdAt: Date.now(),
                    lastAccess: Date.now()
                };

                const eventUserRef = ref(database, `eventUsers/${eventId}/${anonymousId}`);
                await set(eventUserRef, anonymousUser);
            }

            const eventData = {
                meta: {
                    name: eventName.trim(),
                    description: description.trim(),
                    createdBy: user.uid,
                    participants: participantsMap,
                    createdAt: Date.now()
                },
                expenses: {}
            };

            await createEvent(eventData, eventId);

            if (user) {
                await getOrCreateEventUser(eventId, user.uid, finalOwnerName, false);
            }

            navigate(`/event/${eventId}`);

        } catch (error) {
            console.error('Error creating event:', error);
            alert(t('createEvent.errors.createFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!user) {
        return <AuthRequired message={t('createEvent.errors.authRequired')}/>;
    }

    return (
        <div className="glass-page-bg">
            <AppHeader
                title={t('createEvent.title')}
                subtitle={t('createEvent.subtitle')}
                icon={faCalendarAlt}
                darkMode={darkMode}
                onToggleDarkMode={toggleDarkMode}
            />

            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="card">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('createEvent.form.eventName.label')}
                            </label>
                            <input
                                type="text"
                                value={eventName}
                                onChange={(e) => setEventName(e.target.value)}
                                className="input-field"
                                placeholder={t('createEvent.form.eventName.placeholder')}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('createEvent.form.description.label')} {t('createEvent.form.description.optional')}
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="input-field"
                                rows={3}
                                placeholder={t('createEvent.form.description.placeholder')}
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    <FontAwesomeIcon icon={faUsers} className="mr-2"/>
                                    {t('createEvent.form.participants.label')}
                                </label>
                                <button
                                    type="button"
                                    onClick={handlePasteFromClipboard}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
                                    title={t('createEvent.form.participants.pasteHint')}
                                >
                                    <FontAwesomeIcon icon={faPaste} className="mr-1"/>
                                    {t('createEvent.form.participants.paste')}
                                </button>
                            </div>

                            <div className="space-y-3">
                                {/* Current User */}
                                <div
                                    className="flex items-center p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg gap-2">
                                    <input
                                        type="text"
                                        value={ownerName}
                                        onChange={(e) => { setOwnerName(e.target.value); setOwnerNameEdited(true); }}
                                        className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-white font-medium placeholder-gray-400 min-w-0"
                                        placeholder={getDisplayName(userProfile, user)}
                                    />
                                    <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold uppercase whitespace-nowrap">
                                        {t('createEvent.form.participants.owner')}
                                    </span>
                                </div>

                                {/* Participant Tags + Input */}
                                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                                    <div className="flex flex-wrap items-center gap-2">
                                        {/* Participant Tags */}
                                        {participants.map((participant, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-sm rounded-md"
                                            >
                                                {participant}
                                                <button
                                                    type="button"
                                                    onClick={() => removeParticipant(index)}
                                                    className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} className="text-xs"/>
                                                </button>
                                            </span>
                                        ))}

                                        {/* Input */}
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={currentInput}
                                            onChange={(e) => handleInputChange(e.target.value)}
                                            onKeyDown={handleInputKeyDown}
                                            className="flex-1 min-w-32 border-none outline-none bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                            placeholder={participants.length === 0 
                                                ? t('createEvent.form.participants.placeholder.empty') 
                                                : t('createEvent.form.participants.placeholder.more')}
                                        />

                                        {/* Add Button */}
                                        {currentInput.trim() && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (currentInput.trim()) {
                                                        addParticipant(currentInput.trim());
                                                        setCurrentInput('');
                                                        inputRef.current?.focus();
                                                    }
                                                }}
                                                className="glass-btn-primary !px-3 !py-1 text-sm"
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                                                {t('createEvent.form.participants.add')}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Help Text */}
                                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                    <div className="flex items-center">
                                        <FontAwesomeIcon icon={faKeyboard} className="mr-1"/>
                                        {t('createEvent.form.participants.help')}
                                    </div>
                                    {participants.length > 0 && (
                                        <div className="text-green-600 dark:text-green-400">
                                            ✓ {participants.length} {t('createEvent.form.participants.added', {count: participants.length})}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="glass-btn-primary w-full flex items-center justify-center"
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2"/>
                                    {t('createEvent.buttons.creating')}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faPlus} className="mr-2"/>
                                    {t('createEvent.buttons.create')}
                                </>
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateEventPage;